import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Divider,
    Table,
    TableBody,
    TableContainer,
    Paper,
    CircularProgress,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import UpdateIcon from "@mui/icons-material/Update";
import EmptyJourney from "../EmptyJourney";
import TestCaseCharts from "./components/TestCaseCharts";
import StatsSummary from "components/StatsSummary";
import TestCaseItem from "./components/TestCaseItem";
import SearchBar from "components/common/SearchBar";
import PaginationComponent from "components/PaginationComponent";
import styles from "./styles.module.scss";
import { useJourneyTestCases } from "pages/SingleJourney/queries/useJourneyQuery";
import TableHeader from "./components/TableHeader";
import DeleteConfirmationModal from "components/common/DeleteConfirmationModal";
import {
    useDeleteTestCaseMutation,
    useRunTestCasesMutation,
    invalidateJourneyTestCaseQueries,
} from "./queries";
import { TEST_CASE_STATUS } from "./constants";
import { useDebounce } from "utils/debounce";
import { useQueryClient } from "@tanstack/react-query";
import { socketEventEmitter } from "socket";
import { shouldShowTestGenerationDebug } from "utils/addExternalScripts";
import {
    formatTokenCount,
    formatUsdCost,
    getDiscoveryDebugSummary,
} from "utils/discoveryDebug";

const TestCasesPerPage = 10;

const TestCasesTab = ({
    journeyId,
    onOpenGenerateModal,
    onOpenRegenerateModal,
    isGeneration,
    isGenerateDisabled,
}) => {
    const queryClient = useQueryClient();
    const [checkedTestCases, setCheckedTestCases] = useState([]);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: "created_at",
        direction: "desc",
    });
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState(null);
    const [generationDebug, setGenerationDebug] = useState(null);
    const [showDebug, setShowDebug] = useState(false);

    const debouncedSearch = useDebounce(searchQuery, 200);

    const { data: journeyTestCasesResponse, isLoading: isTestCasesLoading } =
        useJourneyTestCases({
            journeyId,
            page,
            limit: TestCasesPerPage,
            searchQuery: debouncedSearch,
            sortConfig,
        });
        
    const journeyTestCases = journeyTestCasesResponse?.testCases || [];
    const totalTestCases = journeyTestCasesResponse?.total || 0;
    const draftTestCases = journeyTestCases.filter(
        (item) => item.status === TEST_CASE_STATUS.draft
    );
    const journeyTestCaseIdSet = useMemo(
        () => new Set(journeyTestCases.map((item) => item.id)),
        [journeyTestCases]
    );

    const deleteCleanUp = () => {
        setOpenDeleteModal(false);
        setIdToDelete(null);
        setCheckedTestCases([]);
    };

    const { mutateAsync: deleteTestCasesAsync, isPending: isTestCasesDeleting } =
        useDeleteTestCaseMutation({
            journeyId,
            idsToDelete: idToDelete ? [idToDelete] : checkedTestCases,
            onSuccess: deleteCleanUp,
            onError: deleteCleanUp,
        });

    const { mutateAsync: runTestCasesAsync, isPending: isTestCaseRunning } =
        useRunTestCasesMutation(journeyId);

    const handleMultipleRun = async () => {
        if (!checkedTestCases.length || isGeneration) return;
        await runTestCasesAsync(checkedTestCases);
        setCheckedTestCases([]);
    };

    const handleGenerateButtonClick = () => {
        if (isGenerateDisabled || typeof onOpenGenerateModal !== "function") {
            return;
        }
        onOpenGenerateModal();
    };

    const handleRegenerateSelected = () => {
        if (!onOpenRegenerateModal || !checkedTestCases.length) return;
        onOpenRegenerateModal({
            idsToRegenerate: checkedTestCases,
            regenerateAll: false,
            onSuccess: () => setCheckedTestCases([]),
        });
    };

    const handleRegenerateAll = () => {
        if (!onOpenRegenerateModal) return;
        onOpenRegenerateModal({
            regenerateAll: true,
            onSuccess: () => setCheckedTestCases([]),
        });
    };

    const handlePageChange = (_, value) => {
        setPage(value);
    };

    const handleSearch = (_, newValue) => {
        setSearchQuery(newValue?.name?.toLowerCase() || newValue || "");
        setPage(1);
    };

    const handleSingleDelete = (id) => {
        setIdToDelete(id);
        setOpenDeleteModal(true);
    };

    const handleCheckboxChange = (id) => {
        setCheckedTestCases((prev) =>
            prev.includes(id)
                ? prev.filter((journeyTestCaseId) => journeyTestCaseId !== id)
                : [...prev, id]
        );
    };

    useEffect(() => {
        (async () => {
            try {
                const allowed = await shouldShowTestGenerationDebug();
                setShowDebug(allowed);
            } catch {
                setShowDebug(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (!journeyTestCases?.length) return;
        const debugPayload =
            journeyTestCases.find((tc) => tc?.discovery_debug)?.discovery_debug;
        if (debugPayload) {
            setGenerationDebug(debugPayload);
        }
    }, [journeyTestCases]);

    useEffect(() => {
        if (totalTestCases === 0) {
            setGenerationDebug(null);
        }
    }, [totalTestCases]);

    useEffect(() => {
        if (!journeyId) return undefined;

        const handleTaskCompleted = (payload) => {
            if (
                payload?.journeyId !== journeyId ||
                payload?.type !== "Journey Test Cases"
            ) {
                return;
            }
            invalidateJourneyTestCaseQueries(queryClient, journeyId);
            const debugPayload =
                payload?.result?.discovery_debug || payload?.discovery_debug;
            if (debugPayload) {
                setGenerationDebug(debugPayload);
            }
        };

        const handleTaskFailed = (payload) => {
            if (
                payload?.journeyId !== journeyId ||
                payload?.type !== "Journey Test Cases"
            ) {
                return;
            }
            invalidateJourneyTestCaseQueries(queryClient, journeyId);
            if (payload?.discovery_debug) {
                setGenerationDebug(payload.discovery_debug);
            }
        };

        socketEventEmitter.on("taskCompleted", handleTaskCompleted);
        socketEventEmitter.on("taskFailed", handleTaskFailed);

        return () => {
            socketEventEmitter.off("taskCompleted", handleTaskCompleted);
            socketEventEmitter.off("taskFailed", handleTaskFailed);
        };
    }, [journeyId, queryClient]);

    useEffect(() => {
        if (!journeyId) return undefined;

        const handleRealtimeUpdate = (payload) => {
            const type = payload?.type;
            if (type !== "Test Case" && type !== "testCase") {
                return;
            }

            const testCaseId = payload?.testCaseId;
            if (
                testCaseId &&
                journeyTestCaseIdSet.size > 0 &&
                !journeyTestCaseIdSet.has(testCaseId)
            ) {
                return;
            }

            invalidateJourneyTestCaseQueries(queryClient, journeyId);
        };

        socketEventEmitter.on("taskActivated", handleRealtimeUpdate);
        socketEventEmitter.on("taskCompleted", handleRealtimeUpdate);
        socketEventEmitter.on("taskFailed", handleRealtimeUpdate);

        return () => {
            socketEventEmitter.off("taskActivated", handleRealtimeUpdate);
            socketEventEmitter.off("taskCompleted", handleRealtimeUpdate);
            socketEventEmitter.off("taskFailed", handleRealtimeUpdate);
        };
    }, [journeyId, queryClient, journeyTestCaseIdSet]);

    const renderGenerationDebug = () => {
        if (!generationDebug) return null;
        const { video_url, aws_tracing_url, llm_logs = [] } = generationDebug;
        const debugSummary = getDiscoveryDebugSummary(generationDebug);
        return (
            <details style={{ marginBottom: 16 }}>
                <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                    Test generation debug
                </summary>
                <div style={{ padding: 12 }}>
                    {debugSummary ? (
                        <div
                            style={{
                                marginBottom: 12,
                                padding: 12,
                                background: "#f7f9fc",
                                border: "1px solid #e3e8ef",
                                borderRadius: 8,
                            }}
                        >
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                Gemini cost
                            </div>
                            <div>
                                Total: {formatUsdCost(debugSummary.totalUsd)}{" "}
                                {debugSummary.currency}
                            </div>
                            <div>
                                Tokens: {formatTokenCount(debugSummary.inputTokens)} in /{" "}
                                {formatTokenCount(debugSummary.outputTokens)} out
                            </div>
                            <div>
                                Priced calls: {formatTokenCount(debugSummary.pricedCalls)}
                            </div>
                        </div>
                    ) : null}
                    {video_url ? (
                        <div style={{ marginBottom: 12 }}>
                            <div>Discovery video:</div>
                            <video
                                src={video_url}
                                controls
                                style={{ width: "100%", maxWidth: 640 }}
                            />
                        </div>
                    ) : null}
                    {aws_tracing_url ? (
                        <div style={{ marginBottom: 12 }}>
                            <a
                                href={aws_tracing_url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Open trace report
                            </a>
                        </div>
                    ) : null}
                    {llm_logs.length ? (
                        <div style={{ overflowX: "auto" }}>
                            <div style={{ fontWeight: 600, margin: "8px 0" }}>
                                LLM I/O
                            </div>
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            #
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            route
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            cost (usd)
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            request json
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            request ts
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            response json
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            response ts
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            page link
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            image
                                        </th>{" "}
                                    </tr>
                                </thead>
                                <tbody>
                                    {llm_logs.map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={{ padding: 6 }}>
                                                {idx + 1}
                                            </td>
                                            <td
                                                style={{
                                                    padding: 6,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {row.route}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {formatUsdCost(row.llm_cost_usd)}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.request_json ? (
                                                    <a
                                                        href={row.request_json}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        request_json
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.request_timestamp || "-"}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.response_json ? (
                                                    <a
                                                        href={row.response_json}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        response_json
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.response_timestamp || "-"}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.page_link ? (
                                                    <a
                                                        href={row.page_link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {row.page_link}
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.image ? (
                                                    <a
                                                        href={row.image}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        screenshot
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>
            </details>
        );
    };

    if (isTestCasesLoading)
        return (
            <Box className={styles.loaderWrapper}>
                <CircularProgress />
            </Box>
        );

    if (!journeyId) return <EmptyJourney />;

    if (!totalTestCases)
        return (
            <>
                {showDebug ? renderGenerationDebug() : null}
                <EmptyJourney
                    title={
                        isGeneration
                            ? "Test cases are being generated"
                            : "No test cases have been generated for this journey yet."
                    }
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        className={styles.generateButton}
                        onClick={handleGenerateButtonClick}
                        disabled={isGeneration || isGenerateDisabled}
                    >
                        {isGeneration ? "GENERATING..." : "GENERATE TEST CASES"}
                    </Button>
                </EmptyJourney>
            </>
        );

    return (
        <Box className={styles.container}>
            {showDebug ? renderGenerationDebug() : null}
            <Box className={styles.details}>
                <Box className={styles.titlleWrapper}>
                    <Typography className={styles.title}>Details</Typography>

                    <Button
                        className={styles.regenerateButton}
                        onClick={handleRegenerateAll}
                        variant="contained"
                        color="secondary"
                        disabled={isGeneration}
                    >
                        REGENERATE TESTS
                    </Button>
                </Box>
                <TestCaseCharts
                    testCasesData={journeyTestCases}
                    testCasesTotal={totalTestCases}
                    draftTestCases={draftTestCases}
                />
            </Box>
            <Divider className={styles.divider} />
            <Box className={styles.statsSection}>
                <StatsSummary
                    title="Total Test Cases"
                    value={totalTestCases}
                    color="primary"
                />
            </Box>
            <Box className={styles.actionsContainer}>
                <Box className={styles.settingsContainer}>
                    {checkedTestCases.length > 0 && (
                        <Box className={styles.itemSettings}>
                            <Button
                                className={styles.runButton}
                                variant="contained"
                                color="secondary"
                                onClick={handleMultipleRun}
                                disabled={isTestCaseRunning || isGeneration}
                            >
                                <PlayCircleOutlineOutlinedIcon
                                    className={styles.runButtonIcon}
                                    color="#fff"
                                />
                                RUN SELECTED
                            </Button>
                            <Button
                                className={styles.regenerateButton}
                                variant="contained"
                                color="secondary"
                                onClick={handleRegenerateSelected}
                                disabled={isGeneration}
                            >
                                <UpdateIcon
                                    className={styles.regenerateButtonIcon}
                                    color="#fff"
                                />
                                REGENERATE SELECTED
                            </Button>
                            <Button
                                className={styles.deleteButton}
                                variant="outlined"
                                color="error"
                                onClick={() => setOpenDeleteModal(true)}
                            >
                                <DeleteOutlineOutlinedIcon
                                    className={styles.deleteButtonIcon}
                                    color="error"
                                />
                                DELETE
                            </Button>
                        </Box>
                    )}
                    <Box className={styles.searchWrapper}>
                        <SearchBar list={journeyTestCases} handleSearch={handleSearch} />
                    </Box>
                </Box>
            </Box>
            <TableContainer className={styles.tableContainer} component={Paper}>
                <Table className={styles.table} stickyHeader>
                    <TableHeader
                        checked={checkedTestCases}
                        setSortConfig={setSortConfig}
                        setCheckedTestCases={setCheckedTestCases}
                        journeyTestCases={journeyTestCases}
                        sortConfig={sortConfig}
                        setPage={setPage}
                    />
                    <TableBody>
                        {journeyTestCases.map((item) => (
                            <TestCaseItem
                                key={item.id}
                                testCase={item}
                                checkedIds={checkedTestCases}
                                handleCheckboxChange={handleCheckboxChange}
                                handleOpenDeleteDialog={handleSingleDelete}
                                journeyId={journeyId}
                                onOpenRegenerateModal={onOpenRegenerateModal}
                                disableActions={isGeneration}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box mr={2}>
                <PaginationComponent
                    page={page}
                    totalItems={totalTestCases}
                    itemsPerPage={TestCasesPerPage}
                    count={Math.ceil(totalTestCases / TestCasesPerPage)}
                    handlePageChange={handlePageChange}
                />
            </Box>
            <DeleteConfirmationModal
                deleteOpen={openDeleteModal}
                handleClose={deleteCleanUp}
                idsToDelete={idToDelete ? [idToDelete] : checkedTestCases}
                isLoading={isTestCasesDeleting}
                essanceName={"test case"}
                handleDelete={deleteTestCasesAsync}
            />
        </Box>
    );
};

export default TestCasesTab;
